import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Parse } from "../../lib/parse";
import { useBrandingConfig } from "../../hooks/useBrandingConfig";
import TopNav from "../ui/TopNav";
import Sidebar from "../ui/Sidebar";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Button from "../ui/Button";

const OPEN_GLOBAL_CREATE_CONTACT_MODAL_EVENT = "dana:open-create-contact-modal";
const CONTACT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REQUEST_SIGN_PDF_PATH_SEGMENT = "/requestSignPdf/";
const SIGNATURE_PDF_PATH_SEGMENT = "/signaturePdf/";
const REQUEST_SIGN_HINT_HIDE_KEY = "dana_requestsignpdf_sidebar_hint_hide";
const REQUEST_SIGN_HINT_SEEN_KEY = "dana_requestsignpdf_sidebar_hint_seen";
const SIGNATURE_HINT_HIDE_KEY = "dana_signaturepdf_sidebar_hint_hide";
const SIGNATURE_HINT_SEEN_KEY = "dana_signaturepdf_sidebar_hint_seen";
const MOBILE_SIDEBAR_BREAKPOINT = 900;

const parseStoredJson = (key) => {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

function AppShell({ children }) {
  const { t } = useTranslation();
  
  // Get tenantId from localStorage
  const getTenantId = () => {
    try {
      const extClassRaw = localStorage.getItem("Extand_Class");
      const extClass = extClassRaw ? JSON.parse(extClassRaw) : null;
      return Array.isArray(extClass) ? extClass?.[0]?.TenantId?.objectId : null;
    } catch (error) {
      return null;
    }
  };
  
  const tenantId = getTenantId();
  const { branding } = useBrandingConfig(tenantId);
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ Name: "", Email: "", Phone: "" });
  const [contactFormError, setContactFormError] = useState("");
  const [contactFormFieldErrors, setContactFormFieldErrors] = useState({ Name: "", Email: "" });
  const [isRequestSignHintOpen, setIsRequestSignHintOpen] = useState(false);
  const [doNotShowRequestSignHintAgain, setDoNotShowRequestSignHintAgain] = useState(false);
  const [isSignatureHintOpen, setIsSignatureHintOpen] = useState(false);
  const [doNotShowSignatureHintAgain, setDoNotShowSignatureHintAgain] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const previousPathRef = useRef("");

  // Apply header bar color from branding
  useEffect(() => {
    if (branding?.headerBarColor) {
      document.documentElement.style.setProperty('--header-bar-color', branding.headerBarColor);
    }
  }, [branding?.headerBarColor]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_SIDEBAR_BREAKPOINT}px)`);
    const syncViewport = (event) => {
      const matches = Boolean(event?.matches ?? mediaQuery.matches);
      setIsMobileViewport(matches);
      if (!matches) {
        setIsMobileSidebarOpen(false);
      }
    };
    syncViewport();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
      return () => mediaQuery.removeEventListener("change", syncViewport);
    }
    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location?.pathname]);

  useEffect(() => {
    if (!isMobileViewport) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    if (isMobileSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalOverflow || "";
    }
    return () => {
      document.body.style.overflow = originalOverflow || "";
    };
  }, [isMobileViewport, isMobileSidebarOpen]);

  useEffect(() => {
    if (!isMobileSidebarOpen) {
      return undefined;
    }
    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    localStorage.setItem("dana_sidebar_collapsed", isSidebarCollapsed ? "1" : "0");
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const currentPath = String(location?.pathname || "");
    const previousPath = String(previousPathRef.current || "");
    const isRequestSignPdfRoute = currentPath.includes(REQUEST_SIGN_PDF_PATH_SEGMENT);
    const wasRequestSignPdfRoute = previousPath.includes(REQUEST_SIGN_PDF_PATH_SEGMENT);
    const isSignaturePdfRoute =
      currentPath.includes(SIGNATURE_PDF_PATH_SEGMENT) && !currentPath.endsWith("/completed");
    const wasSignaturePdfRoute =
      previousPath.includes(SIGNATURE_PDF_PATH_SEGMENT) && !previousPath.endsWith("/completed");

    if (isRequestSignPdfRoute && !wasRequestSignPdfRoute) {
      setIsSidebarCollapsed(true);
      const shouldHideHint = localStorage.getItem(REQUEST_SIGN_HINT_HIDE_KEY) === "1";
      const hasSeenHint = localStorage.getItem(REQUEST_SIGN_HINT_SEEN_KEY) === "1";
      if (!shouldHideHint && !hasSeenHint) {
        setIsRequestSignHintOpen(true);
        localStorage.setItem(REQUEST_SIGN_HINT_SEEN_KEY, "1");
      }
    }
    if (isSignaturePdfRoute && !wasSignaturePdfRoute) {
      setIsSidebarCollapsed(true);
      const shouldHideHint = localStorage.getItem(SIGNATURE_HINT_HIDE_KEY) === "1";
      const hasSeenHint = localStorage.getItem(SIGNATURE_HINT_SEEN_KEY) === "1";
      if (!shouldHideHint && !hasSeenHint) {
        setIsSignatureHintOpen(true);
        localStorage.setItem(SIGNATURE_HINT_SEEN_KEY, "1");
      }
    }

    if (!isRequestSignPdfRoute) {
      setIsRequestSignHintOpen(false);
      setDoNotShowRequestSignHintAgain(false);
    }
    if (!isSignaturePdfRoute) {
      setIsSignatureHintOpen(false);
      setDoNotShowSignatureHintAgain(false);
    }

    previousPathRef.current = currentPath;
  }, [location?.pathname]);

  const dismissRequestSignHint = () => {
    if (doNotShowRequestSignHintAgain) {
      localStorage.setItem(REQUEST_SIGN_HINT_HIDE_KEY, "1");
    }
    setIsRequestSignHintOpen(false);
    setDoNotShowRequestSignHintAgain(false);
  };
  const dismissSignatureHint = () => {
    if (doNotShowSignatureHintAgain) {
      localStorage.setItem(SIGNATURE_HINT_HIDE_KEY, "1");
    }
    setIsSignatureHintOpen(false);
    setDoNotShowSignatureHintAgain(false);
  };

  useEffect(() => {
    const handleOpenCreateContact = () => {
      setContactForm({ Name: "", Email: "", Phone: "" });
      setContactFormError("");
      setContactFormFieldErrors({ Name: "", Email: "" });
      setIsCreateContactModalOpen(true);
    };
    window.addEventListener(OPEN_GLOBAL_CREATE_CONTACT_MODAL_EVENT, handleOpenCreateContact);
    return () => {
      window.removeEventListener(OPEN_GLOBAL_CREATE_CONTACT_MODAL_EVENT, handleOpenCreateContact);
    };
  }, []);

  const closeCreateContactModal = () => {
    if (isSavingContact) {
      return;
    }
    setIsCreateContactModalOpen(false);
    setContactFormError("");
    setContactFormFieldErrors({ Name: "", Email: "" });
    setContactForm({ Name: "", Email: "", Phone: "" });
  };

  const saveGlobalContact = async () => {
    const name = String(contactForm?.Name || "").trim();
    const email = String(contactForm?.Email || "")
      .trim()
      .toLowerCase();
    const phone = String(contactForm?.Phone || "").trim();

    const fieldErrors = { Name: "", Email: "" };
    if (!name) {
      fieldErrors.Name = t("input-required", { defaultValue: "Please fill out this field." });
    }
    if (!email) {
      fieldErrors.Email = t("input-required", { defaultValue: "Please fill out this field." });
    } else if (!CONTACT_EMAIL_REGEX.test(email)) {
      fieldErrors.Email = t("email-error", { defaultValue: "Please enter a valid email address." });
    }
    if (fieldErrors.Name || fieldErrors.Email) {
      setContactFormFieldErrors(fieldErrors);
      setContactFormError("");
      return;
    }

    setIsSavingContact(true);
    setContactFormError("");
    setContactFormFieldErrors({ Name: "", Email: "" });
    try {
      const extClass = parseStoredJson("Extand_Class");
      const firstExt = Array.isArray(extClass) ? extClass?.[0] || {} : {};
      let effectiveTenantId = String(firstExt?.TenantId?.objectId || localStorage.getItem("TenantId") || "").trim();
      if (!effectiveTenantId) {
        const currentUserId = Parse.User.current()?.id || "";
        if (currentUserId) {
          try {
            const tenantPayload = await Parse.Cloud.run("gettenant", { userId: currentUserId });
            effectiveTenantId = String(tenantPayload?.objectId || tenantPayload?.id || "").trim();
          } catch (tenantError) {
            effectiveTenantId = "";
          }
        }
      }

      const createPayload = { name, email, phone };
      if (effectiveTenantId) {
        createPayload.tenantId = effectiveTenantId;
      }
      await Parse.Cloud.run("savecontact", createPayload);
      window.dispatchEvent(
        new CustomEvent("dana:contact-created", {
          detail: { name, email, phone }
        })
      );
      closeCreateContactModal();
    } catch (error) {
      const message = String(error?.message || "");
      if (message.toLowerCase().includes("already exists")) {
        setContactFormError(t("add-signer-alert", { defaultValue: "Contact already exists!" }));
      } else {
        setContactFormError(
          message ||
            t("something-went-wrong-mssg", {
              defaultValue: "Something went wrong."
            })
        );
      }
    } finally {
      setIsSavingContact(false);
    }
  };

  return (
    <div
      className={`dana-shell ${isSidebarCollapsed ? "is-sidebar-collapsed" : ""} ${isMobileSidebarOpen ? "is-mobile-sidebar-open" : ""}`}
    >
      <TopNav
        showMobileMenuButton={isMobileViewport}
        isMobileSidebarOpen={isMobileSidebarOpen}
        onToggleMobileSidebar={() => setIsMobileSidebarOpen((value) => !value)}
      />
      {isMobileViewport && isMobileSidebarOpen ? (
        <button
          type="button"
          className="dana-mobile-sidebar-backdrop"
          aria-label={t("new-ui.sidebar.close-navigation", { defaultValue: "Close navigation" })}
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      ) : null}
      <div className="dana-layout">
        <aside className={`dana-layout-sidebar relative ${isMobileViewport ? "is-mobile-drawer" : ""}`}>
          <Sidebar
            isCollapsed={isMobileViewport ? false : isSidebarCollapsed}
            onToggleCollapse={() => {
              if (!isMobileViewport) {
                setIsSidebarCollapsed((value) => !value);
              }
            }}
            onNavigate={() => setIsMobileSidebarOpen(false)}
          />
        </aside>
        <main className="dana-layout-main">{children}</main>
      </div>
      <Modal
        isOpen={isCreateContactModalOpen}
        title={t("add-contact", { defaultValue: "Add contact" })}
        onClose={closeCreateContactModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            id="global-contact-name"
            label={t("name", { defaultValue: "Name" })}
            value={contactForm.Name}
            error={contactFormFieldErrors.Name}
            onChange={(event) => {
              setContactForm((current) => ({ ...current, Name: event.target.value }));
              setContactFormError("");
              setContactFormFieldErrors((current) => ({ ...current, Name: "" }));
            }}
            maxLength={120}
          />
          <Input
            id="global-contact-email"
            type="email"
            label={t("email", { defaultValue: "Email" })}
            value={contactForm.Email}
            error={contactFormFieldErrors.Email}
            onChange={(event) => {
              setContactForm((current) => ({
                ...current,
                Email: String(event.target.value || "").toLowerCase().replace(/\s/g, "")
              }));
              setContactFormError("");
              setContactFormFieldErrors((current) => ({ ...current, Email: "" }));
            }}
            maxLength={160}
          />
          <Input
            id="global-contact-phone"
            label={t("phone", { defaultValue: "Phone" })}
            value={contactForm.Phone}
            onChange={(event) => {
              setContactForm((current) => ({ ...current, Phone: event.target.value }));
              setContactFormError("");
            }}
            placeholder={t("phone-optional", { defaultValue: "optional" })}
            maxLength={40}
          />
          {contactFormError ? <p className="m-0 text-sm text-app-danger">{contactFormError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeCreateContactModal} disabled={isSavingContact}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" onClick={saveGlobalContact} disabled={isSavingContact}>
              {isSavingContact ? t("loading", { defaultValue: "Loading..." }) : t("save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AppShell;
